import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2, Eye, Mail } from "lucide-react";

const STATUS_STYLE = {
  Live: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  "Configured — Waiting for Trigger": "bg-amber-50 text-amber-700 ring-amber-200",
  Planned: "bg-sky-50 text-sky-700 ring-sky-200",
  "Not Yet Available": "bg-slate-100 text-slate-500 ring-slate-200",
};

export function CustomerAutomationsPanel({ cid, onChange }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [draft, setDraft] = useState({});
  const [preview, setPreview] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/platform/companies/${cid}/customer-automations`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cid]);

  const act = async (fn, msg) => { setBusy(true); try { await fn(); if (msg) toast.success(msg); load(); onChange && onChange(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); } };

  const saveAutomation = (key, patch) => act(() => api.put(`/platform/companies/${cid}/customer-automations/${key}`, patch), "Saved");
  const review = (val) => act(() => api.post(`/platform/companies/${cid}/customer-automations/review`, { reviewed: val }), val ? "Marked complete" : "Reopened");

  const openEditor = (a) => {
    if (open === a.key) { setOpen(null); return; }
    setOpen(a.key); setPreview(null); setTestEmail("");
    setDraft({ subject: a.config.subject, body: a.config.body });
  };

  const runPreview = async (key) => {
    try { const r = await api.post(`/platform/companies/${cid}/customer-automations/${key}/preview`, draft); setPreview(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
  };
  const sendTest = async (key) => {
    if (!testEmail.trim()) { toast.error("Enter a test email"); return; }
    setBusy(true);
    try { const r = await api.post(`/platform/companies/${cid}/customer-automations/${key}/test`, { ...draft, to_email: testEmail.trim(), confirm: true });
      if (r.data.sent) toast.success("Test email sent"); else toast.error(r.data.error || "Could not send"); }
    catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
    finally { setBusy(false); }
  };

  if (!data) return <div data-testid="step-panel-customer_automations" className="text-sm text-slate-400">Loading…</div>;
  const inputCls = "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100";

  return (
    <div data-testid="step-panel-customer_automations">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2"><Send className="w-4 h-4 text-indigo-500" /> Customer Automations</h3>
          <p className="text-sm text-slate-400">Customer-facing emails driven by live operational events. Only live triggers send; the rest are configured and wait.</p>
        </div>
        <button onClick={() => review(!data.reviewed)} disabled={busy} data-testid="cust-auto-review-btn"
          className={`shrink-0 inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold ${data.reviewed ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" : "bg-indigo-600 text-white hover:bg-indigo-500"}`}>
          <CheckCircle2 className="w-4 h-4" /> {data.reviewed ? "Reviewed" : "Mark reviewed"}
        </button>
      </div>

      <div className="rounded-xl ring-1 ring-slate-200 bg-white divide-y divide-slate-100" data-testid="cust-auto-list">
        {data.automations.map((a) => (
          <div key={a.key} data-testid={`cust-auto-${a.key}`}>
            <button onClick={() => openEditor(a)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left">
              <Mail className="w-4 h-4 text-slate-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-800 truncate">{a.label}</div>
                <div className="text-[11px] text-slate-400">{a.group} · trigger: {a.trigger}</div>
              </div>
              <label className="flex items-center gap-1.5 text-xs text-slate-500 mr-2" onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" checked={a.config.enabled} disabled={a.availability === "unavailable"}
                  onChange={(e) => saveAutomation(a.key, { enabled: e.target.checked })}
                  data-testid={`cust-auto-enable-${a.key}`} className="w-4 h-4 accent-indigo-600" /> Enabled
              </label>
              <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${STATUS_STYLE[a.trigger_status] || STATUS_STYLE["Not Yet Available"]}`} data-testid={`cust-auto-status-${a.key}`}>{a.trigger_status}</span>
            </button>

            {open === a.key && (
              <div className="px-5 pb-4 pt-1 bg-slate-50/60 space-y-3" data-testid={`cust-auto-editor-${a.key}`}>
                <div>
                  <label className="text-[11px] font-semibold text-slate-500">Subject</label>
                  <input value={draft.subject || ""} onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))} data-testid={`cust-auto-subject-${a.key}`} className={inputCls} />
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-slate-500">Body</label>
                  <textarea value={draft.body || ""} onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))} rows={6} data-testid={`cust-auto-body-${a.key}`} className={inputCls} />
                  <div className="text-[11px] text-slate-400 mt-1">Variables: {a.vars.map((v) => `{{${v}}}`).join(", ")}</div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button onClick={() => saveAutomation(a.key, { subject: draft.subject, body: draft.body })} disabled={busy} data-testid={`cust-auto-save-${a.key}`} className="rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold px-3 py-1.5">Save template</button>
                  <button onClick={() => runPreview(a.key)} data-testid={`cust-auto-preview-${a.key}`} className="inline-flex items-center gap-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-semibold px-3 py-1.5"><Eye className="w-3.5 h-3.5" /> Preview</button>
                  <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="test@email" data-testid={`cust-auto-test-email-${a.key}`} className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs max-w-[180px]" />
                  <button onClick={() => sendTest(a.key)} disabled={busy} data-testid={`cust-auto-sendtest-${a.key}`} className="inline-flex items-center gap-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs font-semibold px-3 py-1.5">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send test</button>
                </div>
                {preview && (
                  <div className="rounded-lg ring-1 ring-slate-200 bg-white p-3" data-testid={`cust-auto-preview-out-${a.key}`}>
                    <div className="text-[11px] font-semibold text-slate-500 mb-1">Subject: {preview.subject}</div>
                    {preview.missing_required?.length > 0 && <div className="text-[11px] text-rose-600 mb-1">Missing required: {preview.missing_required.join(", ")} — this would block a real send.</div>}
                    <div className="border border-slate-100 rounded-md overflow-hidden" dangerouslySetInnerHTML={{ __html: preview.html }} />
                  </div>
                )}
                {a.config.last_activity && (
                  <div className="text-[11px] text-slate-400">Last: {a.config.last_activity.status}{a.config.last_activity.error ? ` (${a.config.last_activity.error})` : ""}</div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
